/**
 * Dono único de "carregar uma imagem para LER no canvas" (BUG-113).
 *
 * Toda leitura de pixel do app — converter capa pra `data:` antes do raster
 * (`imageConverter`), extrair o tinto da capa (`bookPalette`) — precisa de
 * `crossOrigin="anonymous"`, senão o canvas fica *tainted* e `toDataURL`/
 * `getImageData` estouram. Cada uma escrevia o próprio `new Image()` com o
 * mesmo par `onload`/`onerror`, e nenhuma tinha as duas coisas abaixo.
 *
 * ## 1. O retry, e por que ele muda a CHAVE DE CACHE
 *
 * As capas são servidas pelo `wsrv.nl`, e desde o fix do BUG-99 o Studio pede
 * **exatamente a mesma URL** que a estante já pediu (é o ponto daquele fix:
 * reaproveitar o cache). Só que a estante desenha com `<img>` **sem**
 * `crossOrigin` — `CoverThumb` e `BookCover` —, e uma entrada de cache criada
 * por uma requisição sem CORS **não serve** a uma requisição com CORS: um CDN
 * típico só devolve `Access-Control-Allow-Origin` quando a requisição traz
 * `Origin`, então a entrada gravada não tem o cabeçalho e o navegador a
 * rejeita **em 1-2 ms, sem tocar na rede**.
 *
 * 🔴 **A versão anterior deste módulo curava isso com um ATRASO, e a medição
 * REFUTOU a cura** (18/08). Reproduzido em Chromium com imagem cross-origin,
 * servida com `Cache-Control: max-age=3600` e `ACAO` só quando há `Origin`:
 *
 * | tentativa (mesma URL) | resultado |
 * |---|---|
 * | estante, sem CORS | ✅ 3 ms — e **envenena** a entrada |
 * | Studio, com CORS | ❌ 1 ms |
 * | retry +60 ms | ❌ 1 ms |
 * | retry +300 ms | ❌ 1 ms |
 * | retry +1500 ms | ❌ 1 ms |
 * | após `location.reload()` | ❌ 1 ms |
 * | **retry com chave de cache diferente** | ✅ 2 ms |
 *
 * Ou seja: a entrada envenenada é **grudenta** — não expira com o tempo nem com
 * recarga de página —, e só uma requisição em outra chave volta à rede com
 * `Origin` e recebe o `ACAO`. O servidor confirmou: das quatro tentativas com
 * CORS, **só a de chave diferente chegou nele**.
 *
 * ⚠️ Então o que a 2ª tentativa muda é a **chave**, não o instante. O atraso
 * curto ficou (custa 60 ms e cobre falha transitória de rede, que antes também
 * virava "sem capa" definitivo), mas ele não é o mecanismo da cura.
 *
 * ⚠️ **Por que a 1ª tentativa continua usando a chave compartilhada:** quando o
 * Studio é o primeiro a pedir a capa, a entrada nasce aprovada e a leitura sai
 * em ~1 ms — e, medido, uma entrada COM CORS serve normalmente as requisições
 * SEM CORS da estante depois (a ordem inversa é inofensiva). Ir direto na chave
 * distinta desfaria o BUG-99 e cobraria uma ida à rede fria (p90 **5,7 s**,
 * BUG-84) em toda exportação.
 *
 * ⚠️ **O conserto pela outra ponta foi medido e descartado**: pôr
 * `crossOrigin` também na estante faria a entrada nascer aprovada (e o Studio
 * pegaria a capa em 0 ms), mas a entrada SEM CORS persiste entre sessões de
 * página — no dia do deploy toda capa já cacheada falharia na estante, que
 * cai no `onError` e desenha a placa tipográfica sem tentar de novo. Trocaria
 * um card sem capa por uma estante sem capas. Detalhe em `known-bugs.md`
 * §BUG-113.
 *
 * ## 1b. 🔴 A COLISÃO ACIMA NÃO ACONTECE NOS NOSSOS DOIS CDNs (medido 20/08)
 *
 * A tabela de §1 foi medida contra um servidor **sintético**, montado para o
 * teste: `Cache-Control: max-age=3600` e `ACAO` **só quando há `Origin`**. Os
 * dois hosts que o app realmente usa não se comportam assim.
 *
 * | host | `Access-Control-Allow-Origin` | `Vary` |
 * |---|---|---|
 * | `wsrv.nl` (capas) | `*` **sem** `Origin` e **com** `Origin` | ausente |
 * | `res.cloudinary.com` (avatar, banner) | `*` nos dois casos | ausente |
 *
 * Como a entrada nasce com `ACAO: *` mesmo quando quem a criou foi a `<img>`
 * **sem** `crossOrigin` da estante, não há entrada envenenada a curar. Provado
 * no pipeline real (Chromium, `getOptimizedBase64` + `proxifyImageUrl`, capa de
 * produção): pré-carregar a URL com uma `<img>` sem CORS e **depois** convertê-la
 * com CORS resolve em **18-27 ms** — não falha em 1-2 ms.
 *
 * Consequências, para ninguém decidir em cima da premissa errada:
 *
 *  - **`corsRetryUrl` é inerte no caminho de hoje.** Fica porque a 2ª tentativa
 *    ainda cobre falha transitória e porque o mecanismo é o certo para um host
 *    que varie por `Origin` — mas ele **não** é o que faz a capa aparecer.
 *  - **A chave nova custa uma ida à rede fria.** Medido: abortar o download não
 *    deixa a transformação cacheada no `wsrv.nl` (`X-Cache-Status: MISS` no
 *    pedido seguinte). Repetir em chave distinta paga o transform inteiro de
 *    novo — é por isso que o timeout continua NÃO repetindo aqui.
 *  - **Quem cobre a capa perdida é o chamador**, com uma re-tentativa fora do
 *    caminho crítico: `useShareableImages` (BUG-158). Antes dela, uma conversão
 *    que falhasse ficava sem capa pelo resto da sessão do Studio.
 *
 * ## 2. O teto
 *
 * `<img>` que nunca dispara `onload` nem `onerror` (rede suspensa em segundo
 * plano no WebKit, CDN que segura a conexão) deixava a promise sem assentar
 * **para sempre** — e, como o chamador agrega com `Promise.all`, prendia o
 * lote inteiro de capas. É o BUG-107 aplicado a imagem: promise memoizada ou
 * agregada sem teto condena a montagem toda. Aqui o teto rejeita e o card sai
 * degradado, que é o contrato desta tela.
 */

/**
 * Atraso antes da 2ª tentativa.
 *
 * ⚠️ **Não é ele que cura a colisão de CORS** — medido, nem 1500 ms curam (ver
 * o cabeçalho). Ele existe porque a 2ª tentativa também cobre falha transitória
 * de rede, e pedir de novo no mesmo tick não dá ao transporte chance nenhuma.
 * 60 ms é imperceptível dentro do orçamento de 2,5 s da casca.
 */
const RETRY_DELAY_MS = 60;

/**
 * A 2ª tentativa pede a MESMA imagem numa chave de cache diferente — é isto que
 * cura a colisão (medido: única das quatro tentativas com CORS que chegou ao
 * servidor).
 *
 * Varia o `v` que o projeto já usa como chave de cache em vez de introduzir um
 * parâmetro novo: `v` é um valor arbitrário do nosso lado (`proxifyBookCover`
 * grava `v=<bookId>`), então sufixá-lo não depende de o `wsrv.nl` tolerar uma
 * chave desconhecida. **Isto NÃO reabre o BUG-99:** aquele defeito era
 * sobrescrever o `v` na 1ª tentativa, tornando fria toda exportação; aqui a 1ª
 * tentativa continua intacta e a variação só acontece depois de uma falha.
 *
 * `data:`/`blob:` saem inalterados — não têm CORS a colidir e não têm cache a
 * variar.
 */
export function corsRetryUrl(src: string): string {
  if (src.startsWith("data:") || src.startsWith("blob:")) return src;
  try {
    const base = typeof location !== "undefined" ? location.href : "http://localhost/";
    const u = new URL(src, base);
    const current = u.searchParams.get("v");
    u.searchParams.set("v", current ? `${current}_cors` : "cors");
    return u.toString();
  } catch {
    // URL que não parseia: sufixo cru. Pior caso é a 2ª tentativa repetir a
    // chave e falhar igual — nunca um erro novo.
    return `${src}${src.includes("?") ? "&" : "?"}v=cors`;
  }
}

/**
 * Teto de cada tentativa.
 *
 * Generoso de propósito: o cold miss do `wsrv.nl` foi medido em p90 **5,7 s**
 * no BUG-84, e um teto abaixo disso transformaria capa lenta em capa perdida —
 * trocaria este bug pelo vizinho. Ele existe contra o `<img>` que nunca
 * assenta, não contra o `<img>` devagar.
 */
const LOAD_TIMEOUT_MS = 8000;

/**
 * Estourou o teto (rede lenta) — distinto de "falhou" (colisão de CORS, 404,
 * erro transitório), que assenta em 1-2 ms.
 *
 * A distinção existe porque as duas pedem respostas OPOSTAS: falha rápida
 * merece a 2ª tentativa em outra chave; timeout significa que a rede está
 * devagar, e pedir de novo numa chave FRIA só piora — dobra a espera para
 * chegar ao mesmo lugar. Sem isto o pior caso do módulo era 2×8 s, e um teto
 * de 2,5 s a montante disparava antes dele (BUG-143).
 */
class ImageLoadTimeout extends Error {}

/**
 * Pior caso de `loadCorsImage`, em ms: um teto de tentativa + o atraso do retry
 * + folga para a conversão em canvas que vem depois.
 *
 * Só **um** teto porque timeout não repete (ver `loadCorsImage`) — é isso que
 * mantém o número fechado e pequeno o bastante para alguém confiar nele.
 */
export const CORS_IMAGE_WORST_CASE_MS = 8000 + 60 + 250;

/**
 * Backstop de quem ESPERA pela conversão da capa (hoje o Share Studio).
 *
 * 🔴 **Mora aqui, e não na tela, de propósito.** A invariante é uma relação
 * entre dois números — *o backstop tem de ser maior que o pior caso que ele
 * protege* — e enquanto eles viviam em módulos diferentes ela não tinha onde
 * ser escrita nem testada. Foi assim que o teto do Studio (2500) acabou
 * **abaixo** do pior caso do carregamento, disparando antes do mecanismo que
 * deveria cobrir e transformando o backstop no caminho principal: card
 * capturado sem capa, com o botão habilitado (BUG-143).
 *
 * A margem existe para a conversão WebP em canvas que roda depois do load.
 */
export const COVER_CONVERSION_BACKSTOP_MS = CORS_IMAGE_WORST_CASE_MS + 1500;

function loadOnce(src: string, timeoutMs: number): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Obrigatório: sem isto o canvas fica tainted e a leitura de pixel estoura.
    img.crossOrigin = "anonymous";

    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      // Solta os handlers e o decode em andamento. Sem o `src = ""` o
      // navegador segue baixando uma imagem que ninguém mais espera.
      img.onload = null;
      img.onerror = null;
      img.src = "";
      reject(new ImageLoadTimeout("Tempo esgotado ao carregar imagem."));
    }, timeoutMs);

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn();
    };

    img.onload = () => finish(() => resolve(img));
    img.onerror = () => finish(() => reject(new Error("Falha ao carregar imagem.")));
    img.src = src;
  });
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Carrega `src` pronta para leitura de pixel, com uma segunda tentativa.
 *
 * Rejeita só quando as DUAS tentativas falham — o chamador degrada a partir
 * daí (capa tipográfica, papel padrão), nunca trava.
 */
export async function loadCorsImage(
  src: string,
  { timeoutMs = LOAD_TIMEOUT_MS, retryDelayMs = RETRY_DELAY_MS } = {},
): Promise<HTMLImageElement> {
  try {
    return await loadOnce(src, timeoutMs);
  } catch (err) {
    // ⚠️ Timeout NÃO tenta de novo, e isso é o oposto do instinto.
    //
    // A 2ª tentativa existe para a colisão de cache CORS, que falha em 1-2 ms,
    // e para erro transitório — as duas assentam rápido. Timeout diz outra
    // coisa: a rede está devagar. Repetir numa chave FRIA (que é o que cura a
    // colisão) força ida à rede do zero e **dobra** a espera para chegar ao
    // mesmo lugar, enquanto o usuário olha "Preparando seu card…".
    //
    // É também o que mantém o pior caso deste módulo abaixo do teto que o
    // Studio usa como backstop (`COVER_WAIT_MS`) — ver `CORS_IMAGE_WORST_CASE_MS`.
    if (err instanceof ImageLoadTimeout) throw err;
    // ⚠️ O que faz o retry valer alguma coisa é a CHAVE, não a espera — ver a
    // tabela no cabeçalho. Repetir a mesma URL pega a mesma entrada envenenada
    // e falha igual, por mais que se espere.
    await wait(retryDelayMs);
    return loadOnce(corsRetryUrl(src), timeoutMs);
  }
}
